<template>
  <div>
    <div
      class="por-dashboard"
      v-if="data && Object.keys(data).length > 0 && !isLoading"
    >
      <div class="pull-model">
        <div class="pull-model__view-details">
          <h1>RedStone USDtb Proof of Reserve (PoR)</h1>
          <p>
            Provides transparent verification of assets backing Ethena’s USDtb across multiple custodians and contracts. 
          </p>
          See USDtb and BlackRock BUIDL price feeds <a href="https://app.redstone.finance/app/feeds/?page=1&sortBy=popularity&sortDesc=false&perPage=32&cryptos=BUIDL_FUNDAMENTAL,BUIDL_I_ETHEREUM_FUNDAMENTAL,BUIDL_ARBITRUM_FUNDAMENTAL,BUIDL_I_ETHEREUM_DAILY_ACCRUAL,BUIDL_ARBITRUM_DAILY_ACCRUAL,BUIDL_OPTIMISM_FUNDAMENTAL,BUIDL_OPTIMISM_DAILY_ACCRUAL,BUIDL_POLYGON_FUNDAMENTAL,BUIDL_POLYGON_DAILY_ACCRUAL,BUIDL_SOLANA_FUNDAMENTAL,BUIDL_AVALANCHE_DAILY_ACCRUAL,BUIDL_AVALANCHE_FUNDAMENTAL,BUIDL_SOLANA_DAILY_ACCRUAL,BUIDL_DAILY_INTEREST_ACCRUAL,USDtb_FUNDAMENTAL" target="_blank">here</a>.
        </div>
      </div>
      <span
        class="timestamp"
        v-b-tooltip.hover
        title="Click to refresh the data"
        @click="getData"
      >
        <i class="fa fa-refresh"></i> Updated {{ getFormattedTime }}
        <span>{{ getTimeElapsed }}</span>
      </span>
      <div class="key-metrics-summary">
        <div class="metric-card">
          <div class="metric-title">USDtb Collateralization Ratio</div>
          <div class="metric-value" v-if="collateralizationRatio > 100">
            {{ formatPercentage(collateralizationRatio) }}
          </div>
          <vue-loaders-ball-beat
            v-else
            color="var(--redstone-red-color)"
            scale="0.5"
          ></vue-loaders-ball-beat>
          <div class="metric-subtitle">Coverage of reserves to supply</div>
        </div>
        <div class="metric-card">
          <div class="metric-title">Grand Total</div>
          <div class="metric-value">{{ formatPreciseValue(grandTotal) }}</div>
          <div class="metric-subtitle">Total backing assets</div>
        </div>

        <div class="metric-card">
          <div class="metric-title">Total Supply</div>
          <div class="metric-value">{{ formatPreciseValue(totalSupply, "") }}</div>
          <div class="metric-subtitle">Circulating tokens</div>
        </div>
      </div>

      <div class="por-dashboard__content">
        <div class="two-column">
          <div class="card distribution-chart">
            <div class="card-header">
              <h2>Asset Distribution</h2>
            </div>
            <div class="card-body">
              <apexchart
                type="donut"
                height="300"
                :options="distributionChartOptions"
                :series="distributionChartSeries"
              ></apexchart>
            </div>
          </div>

          <div class="card exchange-chart">
            <div class="card-header">
              <h2>Custodians Distribution</h2>
            </div>
            <div class="card-body">
              <apexchart
                type="bar"
                height="300"
                :options="exchangeChartOptions"
                :series="exchangeChartSeries"
              ></apexchart>
            </div>
          </div>
        </div>
        <div class="card assets-in-motion">
          <div class="card-header">
            <h2>Assets in Motion</h2>
          </div>
          <div class="card-body">
            <div class="assets-motion-container">
              <div
                class="motion-info"
                v-b-tooltip.hover
                title="Subscription & Redemption of BUIDL is not atomic. This means that for periods, usually a few hours, the backing assets can be waiting to receive BUIDL/USDC and as such this is why the assets aren't in the above addresses."
              >
                <i class="fa fa-info-circle"></i>
              </div>
              <div class="aim-value">
                {{ formatPreciseValue(assetsInMotionValue) }}
              </div>
            </div>
          </div>
        </div>
        <div class="card custodians-panel">
          <div class="card-header">
            <h2>Custodians</h2>
            <button class="toggle-panel-btn" @click="toggleCustodiansPanel">
              {{ custodiansCollapsed ? "Show details" : "Hide details" }}
            </button>
          </div>
          <div class="card-body">
            <div class="custodian-tabs">
              <div
                v-for="custodian in visibleCustodians"
                :key="custodian.id"
                class="custodian-tab"
                :class="{ active: activeTab === custodian.id }"
                @click="setActiveTab(custodian.id)"
              >
                <div class="provider-logo">
                  <img v-if="custodian.logo" :src="custodian.logo" :alt="custodian.name" />
                  <i v-else class="fa fa-cube" style="font-size: 30px; color: #565656"></i>
                </div>
                <div class="tab-content">
                  <div class="provider-name">{{ custodian.name }}</div>
                  <div class="provider-balance">
                    {{ formatValue(calculateExchangeTotal(custodian.addressKey)) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="custodian-details-panel" v-show="!custodiansCollapsed">
              <div
                v-for="custodian in visibleCustodians"
                :key="`detail-${custodian.id}`"
                v-show="activeTab === custodian.id"
                class="custodian-detail-view"
              >
                <div class="custodian-header">
                  <span></span>
                  <a
                    v-if="custodian.website"
                    :href="custodian.website"
                    target="_blank"
                    class="provider-link"
                  >
                    Visit {{ custodian.name }} <i class="fa fa-external-link"></i>
                  </a>
                  <span v-else class="provider-identity">{{ custodian.name }}</span>
                </div>

                <div class="details-container">
                  <div class="asset-distribution">
                    <apexchart
                      type="bar"
                      height="300"
                      :options="getCustodianBarChartOptions(custodian.name)"
                      :series="getCustodianBarChartSeries(custodian.addressKey)"
                    ></apexchart>
                    <div
                      v-if="hasSmallValues(custodian.addressKey)"
                      class="small-value-indicator"
                    >
                      * Some values may not be clearly visible due to scale
                      differences.
                    </div>
                  </div>
                  <div class="asset-table">
                    <table class="assets-table">
                      <thead>
                        <tr>
                          <th>Asset</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="asset in assets"
                          :key="`${custodian.id}-${asset.id}`"
                          v-if="checkBalance(custodian.addressKey, asset.balanceKey)"
                        >
                          <td>{{ asset.name }}</td>
                          <td>
                            {{ formatValue(getBalanceValue(asset.balanceKey, custodian.addressKey, 6)) }}
                          </td>
                        </tr>
                        <tr class="subtotal-row">
                          <td>{{ custodian.name }} Total</td>
                          <td>
                            {{ formatPreciseValue(calculateExchangeTotal(custodian.addressKey)) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <vue-loaders-ball-beat
      v-else
      class="table-loader"
      color="var(--redstone-red-color)"
      scale="0.5"
    ></vue-loaders-ball-beat>
  </div>
</template>

<script>
import { mapState } from "vuex";
import VueApexCharts from "vue-apexcharts";
import axios from "axios";
import { format, formatDistanceToNow } from "date-fns";

export default {
  name: "PorDashboard",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      isLoading: false,
      dataTimestamp: null,
      data: null,
      activeTab: null,
      custodiansCollapsed: true,
      custodians: [
        {
          id: "coinbase",
          name: "Coinbase",
          addressKey: "coinbaseWalletAddress",
          logo: "/providers/coinbase-logo.png",
          website: "https://www.coinbase.com/",
          color: "#0052FF"
        },
        {
          id: "komainu",
          name: "Komainu",
          addressKey: "komainuWalletAddress",
          logo: "/providers/komainu.jpeg",
          website: "https://komainu.com/",
          color: "#cb333b"
        },
        {
          id: "zodia",
          name: "Zodia",
          addressKey: "zodiaWalletAddress",
          logo: "/providers/zodia.jpg",
          website: "https://zodia-custody.com/",
          color: "#0cadad"
        },
        {
          id: "copper",
          name: "Copper",
          addressKey: "copperWalletAddress",
          logo: "/providers/copper.png",
          website: "https://copper.com",
          color: "#fdbda4"
        },
        {
          id: "mint",
          name: "Mint",
          addressKey: "mintingContractAddress",
          logo: null,
          website: null,
          color: "#565656"
        }
      ],
      
      // Define assets as structured data
      assets: [
        { id: "usdt", name: "USDT", balanceKey: "usdtBalances", color: "#26A17B" },
        { id: "usdc", name: "USDC", balanceKey: "usdcBalances", color: "#2775ca" },
        { id: "buidl", name: "BUIDL", balanceKey: "buidlBalances", color: "#8530c9" },
        { id: "buidli", name: "BUIDL-I", balanceKey: "buidlIBalances", color: "#fcd319" }
      ],

      distributionChartOptions: {
        chart: {
          type: "donut",
          background: "#ffffff",
          toolbar: {
            show: false,
          },
        },
        colors: ["#26A17B", "#2775ca", "#8530c9", "#fcd319"],
        labels: ["USDT", "USDC", "BUIDL", "BUIDL-I"],
        legend: {
          position: "bottom",
          formatter: function (label, opts) {
            return label;
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return this.formatTooltipValue(val);
            }.bind(this),
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            const series = opts.w.globals.series;
            const seriesIndex = opts.seriesIndex;
            const total = series.reduce((a, b) => a + b, 0);
            const percentage = ((series[seriesIndex] / total) * 100).toFixed(1);

            if (percentage < 1) {
              return "";
            }
            return percentage + "%";
          },
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                name: {
                  show: true,
                },
                value: {
                  show: true,
                  formatter: function (val) {
                    return this.formatValue(val, "$");
                  }.bind(this),
                },
                total: {
                  show: true,
                  showAlways: true,
                  formatter: function (w) {
                    const total = w.globals.seriesTotals.reduce(
                      (a, b) => a + b,
                      0
                    );
                    return this.formatPreciseValue(total, "$");
                  }.bind(this),
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 300,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      },

      exchangeChartOptions: {
        chart: {
          type: "bar",
          height: 350,
          toolbar: {
            show: false,
          },
          background: "#ffffff",
        },
        legend: {
          show: false,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "45%",
            borderRadius: 4,
            distributed: true,
          },
        },
        dataLabels: {
          enabled: false,
          formatter: function (val) {
            return this.formatValue(val, "$");
          }.bind(this),
          offsetY: -20,
          style: {
            fontSize: "12px",
            colors: ["#333"],
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return this.formatTooltipValue(val);
            }.bind(this),
          },
          shared: true,
          intersect: false,
        },
        xaxis: {
          categories: ["Coinbase", "Komainu", "Zodia", "Copper", "Mint"],
          labels: {
            style: {
              colors: ["#0052FF", "#cb333b", "#0cadad", "#fdbda4", "#565656"],
            },
          },
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              return this.formatAxisValue(val);
            }.bind(this),
            style: {
              colors: ["#333333"],
            },
          },
          logarithmic: false,
          forceNiceScale: true,
        },
      },
    };
  },

  computed: {
    ...mapState("manifests", ["manifests"]),
    visibleCustodians() {
      return this.custodians.filter(custodian => 
        this.checkExchangeBalance(custodian.addressKey)
      );
    },

    assetsInMotionValue() {
      if (!this.data || !this.data.usdtbAimValue) return 0;
      return this.convertFromDecimals(this.data.usdtbAimValue, 6);
    },
    
    getFormattedTime() {
      return format(this.dataTimestamp, "dd.MM.yyyy HH:mm");
    },
    
    getTimeElapsed() {
      return formatDistanceToNow(this.dataTimestamp, { addSuffix: true });
    },

    collateralizationRatio() {
      if (!this.data) return 0;
      const totalSupplyValue = this.totalSupply || 1;
      return (this.grandTotal / totalSupplyValue) * 100;
    },

    totalSupply() {
      if (!this.data || !this.data.usdtbTotalSupplyValue) return 0;
      return this.convertFromDecimals(this.data.usdtbTotalSupplyValue, 18);
    },

    usdtExchanges() {
      if (!this.data || !this.data.usdtBalances) return 0;
      return this.getSumForAsset("usdtBalances");
    },

    usdcExchanges() {
      if (!this.data || !this.data.usdcBalances) return 0;
      return this.getSumForAsset("usdcBalances");
    },

    buidlExchanges() {
      if (!this.data || !this.data.buidlBalances) return 0;
      return this.getSumForAsset("buidlBalances");
    },

    buidlIExchanges() {
      if (!this.data || !this.data.buidlIBalances) return 0;
      return this.getSumForAsset("buidlIBalances");
    },

    totalExchanges() {
      return (
        this.usdtExchanges +
        this.usdcExchanges +
        this.buidlExchanges +
        this.buidlIExchanges
      );
    },

    totalMintingContract() {
      if (!this.data) return 0;
      return (
        this.getBalanceValue("usdtBalances", "mintingContractAddress", 6) +
        this.getBalanceValue("usdcBalances", "mintingContractAddress", 6) +
        this.getBalanceValue("buidlBalances", "mintingContractAddress", 6) +
        this.getBalanceValue("buidlIBalances", "mintingContractAddress", 6)
      );
    },

    grandTotal() {
      return this.totalExchanges + this.totalMintingContract;
    },

    distributionChartSeries() {
      const assetValues = this.assets.map(asset => ({
        name: asset.name,
        value: this.getSumForAsset(asset.balanceKey)
      }));
      
      const nonZeroAssets = assetValues.filter((asset) => asset.value > 0);
      return nonZeroAssets.map((asset) => asset.value);
    },

    exchangeChartSeries() {
      const exchangeValues = this.custodians.map(custodian => ({
        name: custodian.name,
        value: this.calculateExchangeTotal(custodian.addressKey)
      }));

      const nonZeroExchanges = exchangeValues.filter(
        (exchange) => exchange.value > 0
      );

      return [
        {
          name: "Total Value",
          data: nonZeroExchanges.map((exchange) => exchange.value),
        },
      ];
    },
  },

  async created() {
    await this.getData();
    if (this.visibleCustodians.length > 0) {
      this.activeTab = this.visibleCustodians[0].id;
    }
  },

  methods: {
    scrollIntoDetails() {
      const targetElement = document.querySelector(".custodian-tab");
      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }, 100);
      }
    },
    
    toggleCustodiansPanel() {
      this.custodiansCollapsed = !this.custodiansCollapsed;
      if (!this.custodiansCollapsed && !this.activeTab && this.visibleCustodians.length > 0) {
        this.setActiveTab(this.visibleCustodians[0].id);
      }
    },
    
    setActiveTab(tabName) {
      this.custodiansCollapsed = false;
      this.activeTab = tabName === this.activeTab ? null : tabName;
      this.scrollIntoDetails();
    },
    
    hasSmallValues(custodianAddress) {
      const assets = this.getNonZeroAssets(custodianAddress);

      if (assets.length <= 1) return false;

      const values = assets.map((asset) => asset.value);
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);
      return maxValue / minValue > 100;
    },

    getCustodianBarChartOptions(custodianName) {
      const custodianObj = this.custodians.find(c => c.name === custodianName);
      if (!custodianObj) return {};
      
      const custodianAddress = custodianObj.addressKey;
      const assets = this.getNonZeroAssets(custodianAddress);

      let minValue = Number.MAX_VALUE;
      assets.forEach((asset) => {
        if (asset.value > 0 && asset.value < minValue) {
          minValue = asset.value;
        }
      });

      let maxValue = 0;
      assets.forEach((asset) => {
        if (asset.value > maxValue) {
          maxValue = asset.value;
        }
      });

      const useLogScale = false;

      return {
        chart: {
          type: "bar",
          background: "#ffffff",
          toolbar: {
            show: false,
          },
          stacked: false,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            borderRadius: 4,
            dataLabels: {
              position: "top",
            },
          },
        },
        colors: assets.map((asset) => {
          const assetObj = this.assets.find(a => a.balanceKey === asset.type);
          return assetObj ? assetObj.color : "#cccccc";
        }),
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return this.formatValue(val, "$");
          }.bind(this),
          offsetY: -20,
          style: {
            fontSize: "12px",
            colors: ["#333"],
          },
        },
        legend: {
          show: false,
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return this.formatTooltipValue(val);
            }.bind(this),
          },
          shared: true,
          intersect: false,
        },
        xaxis: {
          categories: [custodianName],
          labels: {
            style: {
              fontSize: "14px",
              fontWeight: 500,
            },
          },
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              return this.formatAxisValue(val);
            }.bind(this),
            style: {
              colors: ["#333333"],
            },
          },
          logarithmic: useLogScale,
          forceNiceScale: true,
          min: useLogScale ? undefined : 0,
        },
      };
    },

    getCustodianBarChartSeries(custodianAddress) {
      if (!this.data) return [];

      const assets = this.getNonZeroAssets(custodianAddress);

      return assets.map((asset) => {
        const assetObj = this.assets.find(a => a.balanceKey === asset.type);
        return {
          name: assetObj ? assetObj.name : asset.type,
          data: [asset.value],
        };
      });
    },

    getNonZeroAssets(custodianAddress) {
      if (!this.data) return [];

      return this.assets
        .map(asset => ({
          type: asset.balanceKey,
          value: this.getBalanceValue(asset.balanceKey, custodianAddress, 6)
        }))
        .filter(asset => asset.value > 0);
    },

    async getData() {
      const paramsMap = {
        usdtb: {
          token: "USDtb_FUNDAMENTAL",
          sourceMetadata: "ethereum-ethena-usdtb",
        },
      };
      try {
        this.isLoading = true;
        const { data } = await axios.get(
          "https://oracle-gateway-1.a.redstone.finance/data-packages/latest/redstone-primary-prod"
        );
        if (data) {
          const metadata =
            data[paramsMap[this.$route.params.id].token]?.[0]?.dataPoints?.[0]
              ?.metadata?.sourceMetadata?.[
              paramsMap[this.$route.params.id].sourceMetadata
            ];
          this.data = metadata?.intermediateValues || null;
          this.dataTimestamp =
            data["USDtb_FUNDAMENTAL"][0].timestampMilliseconds;
          this.updateDistributionChartOptions();
        } else {
          console.error("No data returned from API");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      this.isLoading = false;
    },
    
    updateDistributionChartOptions() {
      if (!this.data) return;
      const assetValues = this.assets.map(asset => ({
        name: asset.name,
        value: this.getSumForAsset(asset.balanceKey),
        color: asset.color
      }));
      
      const nonZeroAssets = assetValues.filter(asset => asset.value > 0);

      this.distributionChartOptions = {
        ...this.distributionChartOptions,
        labels: nonZeroAssets.map(asset => asset.name),
        colors: nonZeroAssets.map(asset => asset.color),
      };
      const nonZeroExchanges = this.custodians
        .map(custodian => ({
          name: custodian.name,
          value: this.calculateExchangeTotal(custodian.addressKey),
          color: custodian.color
        }))
        .filter(exchange => exchange.value > 0);

      this.exchangeChartOptions = {
        ...this.exchangeChartOptions,
        xaxis: {
          ...this.exchangeChartOptions.xaxis,
          categories: nonZeroExchanges.map(exchange => exchange.name),
        },
        colors: nonZeroExchanges.map(exchange => exchange.color),
      };
    },
    formatNumber(num, options = {}) {
      const {
        prefix = "$",
        decimals = 2,
        showZeroAsZero = true,
        suffix = "",
        formatType = "standard",
        useAbbreviations = true // New option to toggle abbreviations
      } = options;

      if (num === undefined || num === null) {
        return showZeroAsZero ? prefix + "0" + suffix : "0" + suffix;
      }

      const value = parseFloat(num);

      if (formatType === "percentage") {
        return value.toFixed(decimals) + "%";
      }

      const decimalPlaces = formatType === "tooltip" ? 6 : decimals;

      // If abbreviations are disabled, use locale string formatting
      if (!useAbbreviations) {
        return prefix + value.toLocaleString(undefined, {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        }) + suffix;
      }

      // Standard abbreviation formatting
      if (value >= 1000000000000 && formatType === "axis") {
        return (value / 1000000000000).toFixed(decimals) + "T" + suffix;
      } else if (value >= 1000000000) {
        return prefix + (value / 1000000000).toFixed(decimalPlaces) + "B" + suffix;
      } else if (value >= 1000000) {
        return prefix + (value / 1000000).toFixed(decimalPlaces) + "M" + suffix;
      } else if (value >= 1000) {
        return prefix + (value / 1000).toFixed(decimalPlaces) + "K" + suffix;
      } else {
        if (formatType === "axis") {
          return value.toFixed(0) + suffix;
        }
        return prefix + value.toFixed(decimalPlaces) + suffix;
      }
    },
    formatValue(num, prefix = "$") {
      return this.formatNumber(num, {
        prefix,
        decimals: 2,
        formatType: "standard",
        useAbbreviations: true
      });
    },
    formatPreciseValue(num, prefix = "$") {
      return this.formatNumber(num, {
        prefix,
        decimals: 0,
        formatType: "standard",
        useAbbreviations: false
      });
    },

    formatTooltipValue(val) {
      return this.formatNumber(val, {
        prefix: "$",
        decimals: 2,
        formatType: "tooltip"
      });
    },

    formatAxisValue(val) {
      return this.formatNumber(val, {
        prefix: "",
        decimals: 2,
        formatType: "axis"
      });
    },

    formatPercentage(value) {
      return this.formatNumber(value, {
        prefix: "",
        formatType: "percentage",
        decimals: 4
      });
    },

    getBalanceValue(balanceType, addressType, decimals) {
      if (
        !this.data ||
        !this.data[balanceType] ||
        this.data[balanceType][addressType] === undefined
      ) {
        return 0;
      }
      return this.convertFromDecimals(
        this.data[balanceType][addressType],
        decimals
      );
    },

    convertFromDecimals(value, decimals) {
      if (value === undefined || value === null) return 0;
      const valueStr = String(value);
      if (decimals === 18) {
        if (valueStr.length > 15) {
          return parseFloat(valueStr) / Math.pow(10, decimals);
        }
      }
      return parseFloat(valueStr) / Math.pow(10, decimals);
    },

    getSumForAsset(assetType) {
      if (!this.data || !this.data[assetType]) return 0;

      return this.custodians.reduce((sum, custodian) => {
        return sum + this.getBalanceValue(assetType, custodian.addressKey, 6);
      }, 0);
    },

    checkBalance(addressType, balanceType) {
      return (
        this.data &&
        this.data[balanceType] &&
        this.data[balanceType][addressType] !== undefined &&
        parseFloat(this.data[balanceType][addressType]) > 0
      );
    },

    checkExchangeBalance(exchangeType) {
      return this.assets.some(asset => 
        this.checkBalance(exchangeType, asset.balanceKey)
      );
    },

    calculateExchangeTotal(exchangeType) {
      if (!this.data) return 0;

      return this.assets.reduce((total, asset) => {
        return total + this.getBalanceValue(asset.balanceKey, exchangeType, 6);
      }, 0);
    },
  },
};
</script>

<style lang="scss">
  $red-redstone: #ae0822;

  .por-dashboard {
    padding: 20px;

    .pull-model {
      margin: 30px 0;
      padding: 0;

      &__view-details h1 {
        margin: 0;
        margin-bottom: 10px;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .toggle-panel-btn {
      background: none;
      font-size: 12px;
      border: none;
      cursor: pointer;
      color: #777;
      padding: 5px;
      transition: all 0.2s ease;

      &:hover {
        color: $red-redstone;
      }

      i {
        font-size: 14px;
      }
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .key-metrics-summary {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      margin-bottom: 30px;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
      }

      .metric-card {
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        padding: 20px;
        text-align: center;
        position: relative;
        border-top: 5px solid #eee;

        .metric-title {
          font-size: 16px;
          font-weight: 500;
          color: #555;
          margin-bottom: 8px;
        }

        .metric-value {
          font-size: 28px;
          font-weight: 600;
          color: $red-redstone;
          margin-bottom: 8px;
        }

        .metric-subtitle {
          font-size: 14px;
          color: #777;
        }
      }
    }

    .card {
      background-color: #fff;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      border: none;
      border-radius: 8px;

      &-header {
        padding: 16px 20px;
        background-color: #f8f9fa;
        border: none;

        h2 {
          font-size: 14px;
          font-weight: 500;
          margin: 0;
        }
      }

      &-body {
        padding: 20px;
      }
    }

    .two-column {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
      }
    }

    .assets-table {
      width: 100%;
      border-collapse: collapse;

      th,
      td {
        padding: 12px 16px;
        text-align: left;
        border-bottom: 1px solid #eee;
      }

      th {
        font-weight: 500;
        color: #555;
      }

      .subtotal-row td {
        font-weight: 500;
        border-top: 2px solid #eee;
      }

      .total-row td {
        font-weight: 600;
        font-size: 16px;
        background-color: #f8f9fa;
        color: $red-redstone;
      }
    }

    .custodians-panel {
      .card-body {
        padding: 0;
      }

      .custodian-tabs {
        display: flex;
        border-bottom: 1px solid #eee;
        overflow-x: auto;

        &::-webkit-scrollbar {
          height: 4px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        &::-webkit-scrollbar-thumb {
          background: #ddd;
          border-radius: 10px;
        }

        .custodian-tab {
          display: flex;
          align-items: center;
          padding: 15px 20px;
          min-width: 180px;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          transition: all 0.3s ease;

          &:hover {
            background-color: #f9f9f9;
          }

          &.active {
            border-bottom-color: $red-redstone;
            background-color: #fcf9f9;

            .provider-name {
              color: $red-redstone;
              font-weight: 600;
            }
          }

          .provider-logo {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            overflow: hidden;
            margin-right: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #fff;
            border: 1px solid #eee;

            img {
              width: 30px;
              height: 30px;
              object-fit: contain;
            }
          }

          .tab-content {
            display: flex;
            flex-direction: column;

            .provider-name {
              font-weight: 500;
              font-size: 14px;
              margin-bottom: 3px;
            }

            .provider-balance {
              font-size: 13px;
              color: $red-redstone;
            }
          }
        }
      }

      .custodian-details-panel {
        position: relative;

        .custodian-detail-view {
          padding: 20px;

          .custodian-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;

            h3 {
              margin: 0;
              color: $red-redstone;
              font-size: 18px;
              font-weight: 500;
            }

            .provider-link,
            .provider-identity {
              color: $red-redstone;
              text-decoration: none;
              font-size: 14px;

              &:hover {
                text-decoration: underline;
              }
            }

            .provider-identity {
              cursor: default;
              &:hover {
                text-decoration: none;
              }
            }
          }

          .details-container {
            display: flex;
            gap: 30px;

            @media (max-width: 768px) {
              flex-direction: column;
            }

            .asset-distribution {
              flex: 1;
              min-width: 0;
              position: relative;
            }

            .asset-table {
              flex: 1;
              min-width: 0;
            }
          }
        }

        .custodian-empty-state {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 300px;

          .empty-message {
            text-align: center;
            color: #777;

            i {
              font-size: 36px;
              margin-bottom: 10px;
              color: #ddd;
            }

            p {
              margin: 0;
            }
          }
        }
      }
    }

    .timestamp {
      background: #f5f5f5;
      border-radius: 4px;
      padding: 5px 10px;
      font-size: 12px;
      margin-bottom: 20px;
      color: #000;
      display: inline-block;
      cursor: pointer;
      transition: all 0.3s;
      span {
        text-align: right;
        font-size: 8px;
        display: block;
      }
      i {
        transition: all 0.3s;
      }
      &:hover {
        i {
          color: $red-redstone;
        }
      }
    }

    .small-value-indicator {
      position: absolute;
      bottom: -5px;
      left: 0;
      right: 0;
      background-color: rgba(0, 0, 0, 0.05);
      border-radius: 3px;
      padding: 4px 8px;
      font-size: 10px;
      color: #666;
      text-align: center;
    }

    .log-scale-indicator {
      position: absolute;
      top: 10px;
      right: 10px;
      background-color: rgba(174, 8, 34, 0.1);
      color: #ae0822;
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 4px;
      z-index: 10;
    }

    .address-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 16px;
    }

    .address-item {
      display: flex;
      flex-direction: column;
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }

      .address-label {
        font-weight: 500;
        margin-bottom: 4px;
        color: #555;
      }

      .address-value {
        background-color: #f8f9fa;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 14px;
        display: flex;
        align-items: center;

        .address-text {
          flex: 1;
          word-break: break-all;
        }

        .address-link {
          color: $red-redstone;
          margin-left: 8px;

          &:hover {
            opacity: 0.8;
          }
        }
      }
    }
    td:first-child {
      width: 250px;
    }
    td:nth-child(2) {
      color: $red-redstone;
    }

    .subtotal-row td {
      color: $red-redstone;
      font-weight: 600;
    }

    .exchange-group {
      margin-bottom: 30px;

      &:last-child {
        margin-bottom: 20px;
      }

      .exchange-title {
        font-size: 18px;
        font-weight: 500;
        margin: 0 0 15px 0;
        color: $red-redstone;
        padding-bottom: 8px;
      }
    }

    .grand-total {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 24px;
      font-weight: 600;
      padding: 15px 0 0 0;
      margin-top: 10px;

      .total-value {
        margin-left: 15px;
        color: $red-redstone;
      }
    }

    .assets-in-motion {
      .assets-motion-container {
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }

      .info-content {
        display: flex;
        align-items: flex-start;
        flex: 1;

        .info-icon {
          padding-right: 15px;
          font-size: 20px;
          color: $red-redstone;
          padding-top: 2px;
        }

        .info-text {
          flex: 1;

          p {
            margin: 0;
            line-height: 1.5;
            color: #555;
          }
        }
      }

      .aim-value {
        font-size: 24px;
        font-weight: 600;
        color: $red-redstone;
        margin-left: 20px;
        white-space: nowrap;
      }
    }
  }

  .table-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    width: 100%;
  }
</style>